import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ReusableDialog from "../../../../common/dialog/dialog";
import axios from "axios";
import { useSelector } from "react-redux";
import Loading from "../../../../common/loader/loading";

const areas = ["Kitchen", "Downstairs bathroom", "Childs bathroom", "Sleep in bathroom 1", "Sleep in bathroom 2"];
const coldOptions = ["inform to manager"];
const hotOptions = ["inform to manager"];

const WaterTemperature = ({ onClose, setOpenDefaultSnackbar, selectedTask }) => {
  const user = useSelector((state) => state.auth.user);
  const currentTime = new Date().toTimeString().slice(0, 5); // Extracts HH:MM format
  const [records, setRecords] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tempDialogOpenHot, setTempDialogOpenHot] = useState(false);
  const [tempDialogOpenCold, setTempDialogOpenCold] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newRecord, setNewRecord] = useState({
    area: "",
    time: currentTime,
    hot_temp: 0,
    cold_temp: 0,
    hot_action: "",
    cold_action: "",
  });
  const [taskId, setTaskId] = useState(selectedTask?.id);

  useEffect(() => {
    if (!taskId && selectedTask?.id) {
      setTaskId(selectedTask.id);
    }
  }, [selectedTask?.id]); // Run only if `selectedTask.id` is not set

  const today = new Date();
  const currentDate = today.toISOString().split("T")[0];

  const updateSubTask = async (taskId) => {
    if (!taskId) {
      console.error("Task ID is undefined. Cannot update.");
      return;
    }

    try {
      await axios.put(
        `/api/assessments/updateTask/${taskId}`,
        {
          status: "completed",
          checklist: [],
          updated_by: user?.username || "Unknown",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-role": user?.roleid || "",
          },
        }
      );
      setOpenDefaultSnackbar(true);
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  const handleDialogOpen = () => {
    setNewRecord({ time: currentTime, area: "", hot_temp: 0, cold_temp: 0, hot_action: "", cold_action: "" });
    setDialogOpen(true);
  };

  const handleNewRecordChange = (field, value) => {
    setNewRecord((prev) => ({ ...prev, [field]: value }));
  };

  const fetchAllRecords = async () => {
    try {
      const response = await axios.get("/api/waterTemp/temperatureAll");
      setRecords(response.data.temperatures); // Assuming `records` is your state for storing the data
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllRecords();
  }, []);

  const handleAutocompleteChangeHot = (event, newValue) => {
    handleNewRecordChange("hot_action", newValue);

    if (newValue === "other") {
      setTempDialogOpenHot(true);
    }
  };

  const handleAutocompleteChangeCold = (event, newValue) => {
    handleNewRecordChange("cold_action", newValue);

    if (newValue === "other") {
      setTempDialogOpenCold(true);
    }
  };

  const handleDialogSubmit = async () => {
    try {
      // Validate that all required fields are filled
      if (!newRecord.area || !newRecord.time || newRecord.hot_temp === null || newRecord.cold_temp === null) {
        alert("All fields must be filled out.");
        return;
      }

      // Prepare payload with proper data types
      const payload = {
        time: newRecord.time,
        area: newRecord.area,
        hot_temp: parseInt(newRecord.hot_temp),
        cold_temp: parseInt(newRecord.cold_temp),
        hot_action: newRecord.hot_action,
        cold_action: newRecord.cold_action,
        completed_by: user.username, // Include the completed_by field
      };

      // Send data to the API
      const response = await axios.post("/api/waterTemp/temperature", payload);

      if (response.data.success) {
        console.log("Data successfully submitted:", response.data);

        setRecords([...records, payload]); // Add the record to the table
        setDialogOpen(false);
      } else {
        console.error("Submission failed:", response.data.message);
      }
    } catch (error) {
      console.error("Error submitting record:", error);
      alert("An error occurred while submitting the record.");
    }
  };

  // Check if all fields are filled
  const isSubmitDisabled = !newRecord.area || !newRecord.time || !newRecord.hot_temp || !newRecord.cold_temp;

  if (loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: 4, m: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Tasks'
        size='small'
      />
      {/* Header */}
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 2 }}>
        Water Temperature Record Sheet
      </Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575", mb: 1 }}>
        Date: {currentDate}
      </Typography>
      <Typography variant='body2' sx={{ color: "#757575", mb: 3 }}>
        Hot and cold water temperatures are recorded to reduce the risk from production of legionella. Hot water
        temperatures must be kept below 43 as there is a risk of scalding. Cold water temperature must be kept below 20.
        This check should be carried out weekly (unless instructed otherwise).
      </Typography>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Time
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Area Temperature Taken In
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Hot Temperature
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Cold Temperature
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Hot Action
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}>
              Cold Action
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records?.map((record, index) => (
            <TableRow key={index}>
              <TableCell>{record.time}</TableCell>
              <TableCell>{record.area}</TableCell>
              <TableCell>
                <Typography sx={{ color: record.hot_temp > 43 ? "red" : "blue" }}>{record.hot_temp}</Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ color: record.cold_temp > 23 ? "red" : "blue" }}>{record.cold_temp}</Typography>
              </TableCell>
              <TableCell>{record?.hot_action || "Temprature is correct "}</TableCell>
              <TableCell>{record?.cold_action || "Temprature is correct "}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Add Record Link */}
      <Box sx={{ mt: 2, mb: 4 }}>
        <Button
          onClick={handleDialogOpen}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
          disabled={!areas.some((area) => !records.some((record) => record.area === area))}
        >
          <AddIcon fontSize='small' /> Add another record
        </Button>
      </Box>

      {/* Dialog */}
      <ReusableDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title='Add A Water Temperature Record'
        onSubmit={handleDialogSubmit}
        submitText='Submit'
        cancelText='Cancel'
        disable={isSubmitDisabled}
      >
        <Grid container spacing={2}>
          <Grid item sm={6}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Area</Typography>
            <Autocomplete
              id='area-temperature'
              options={areas.filter((area) => !records.some((record) => record.area === area))}
              value={newRecord.area}
              onChange={(event, newValue) => handleNewRecordChange("area", newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              fullWidth
              sx={{ mb: 2 }}
              disabled={!areas.some((area) => !records.some((record) => record.area === area))} // Disable if all areas are selected
            />
          </Grid>
          <Grid item sm={4}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Time</Typography>
            <TextField
              fullWidth
              type='time'
              value={newRecord.time}
              onChange={(e) => handleNewRecordChange("time", e.target.value)}
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item sm={6}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Hot Temperature</Typography>
            <Grid container spacing={1}>
              <Grid item sm={4}>
                <TextField
                  fullWidth
                  type='number'
                  value={newRecord.hot_temp}
                  onChange={(e) => handleNewRecordChange("hot_temp", e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item sm={8}>
                {newRecord.hot_temp > 43 && (
                  <Typography sx={{ fontSize: "10px", color: "red", mt: 3 }}>
                    Hot Temperature Level is Too High
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>

          <Grid item sm={6}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Cold Temperature</Typography>
            <Grid container spacing={1}>
              <Grid item sm={4}>
                <TextField
                  fullWidth
                  type='number'
                  value={newRecord.cold_temp}
                  onChange={(e) => handleNewRecordChange("cold_temp", e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item sm={8}>
                {newRecord.cold_temp > 20 && (
                  <Typography sx={{ fontSize: "10px", color: "red", mt: 3 }}>
                    Cold Temperature Level is Too Low
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item sm={6}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Hot Action</Typography>
            <Autocomplete
              id='hot_action'
              options={hotOptions}
              value={newRecord.hot_action}
              onChange={handleAutocompleteChangeHot}
              renderInput={(params) => <TextField {...params} placeholder='Select' fullWidth />}
              fullWidth
              sx={{ mb: 2 }}
              disabled={newRecord.hot_temp < 43}
            />
          </Grid>

          <Grid item sm={6}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Cold Action</Typography>
            <Autocomplete
              id='cold_action'
              options={coldOptions}
              value={newRecord.cold_action}
              onChange={handleAutocompleteChangeCold}
              renderInput={(params) => <TextField {...params} placeholder='Select' fullWidth />}
              fullWidth
              disabled={newRecord.cold_temp < 20}
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
      </ReusableDialog>

      <Dialog open={tempDialogOpenHot} onClose={() => setTempDialogOpenHot(false)}>
        <DialogTitle>If the reason is "Other," what action was taken?</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={newRecord.hot_action}
            onChange={(e) => handleNewRecordChange("hot_action", e.target.value)}
            placeholder='Enter the other action...'
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={() => setTempDialogOpenHot(false)}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tempDialogOpenCold} onClose={() => setTempDialogOpenCold(false)}>
        <DialogTitle>If the reason is "Other," what action was taken?</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={newRecord.cold_action}
            onChange={(e) => handleNewRecordChange("cold_action", e.target.value)}
            placeholder='Enter the other action...'
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={() => setTempDialogOpenCold(false)}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Buttons */}
      <Grid item xs={12} mt={2}>
        <Grid container spacing={1} className='submit-button-container'>
          <Grid item>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              className='submit-button'
              sx={{ textTransform: "none" }}
              onClick={() => {
                onClose();
                !areas.some((area) => !records.some((record) => record.area === area)) && updateSubTask(taskId);
              }}
            >
              Done
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WaterTemperature;
